class ProductGalleryInfiniteSlider extends SliderComponent {
  constructor() {
    super();
    if (!this.slider || !this.nextButton) return;

    this.isThumbnailSlider = this.id.startsWith('GalleryThumbnails');
    if (this.isThumbnailSlider) return; 

    this.enableSliderLooping = true;
    this.loopSetWidth = 0;
    this.isAdjustingScroll = false;
    this.scrollPositionInitialized = false;

    this.measureLoopSet = this.measureLoopSet.bind(this);
    this.onInfiniteScroll = this.onInfiniteScroll.bind(this);

    this.cloneSlidesForLoop();

    this.loopResizeObserver = new ResizeObserver(this.measureLoopSet);
    this.loopResizeObserver.observe(this.slider);
    this.measureLoopSet();

    this.slider.addEventListener('scroll', this.onInfiniteScroll, { passive: true });
  }
  cloneSlidesForLoop() {
    const slides = this.slider.querySelectorAll('[id^="Slide-"]:not([data-clone])');
    if (slides.length < 2) return;
    if (this.slider.querySelector('[data-clone]')) return;

    for (let cloneIndex = 1; cloneIndex <= 2; cloneIndex++) {
      slides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-clone', String(cloneIndex));
        clone.setAttribute('aria-hidden', 'true');
        clone.id = `${slide.id}-clone-${cloneIndex}`;
        clone.classList.remove('is-active', 'is-adjacent', 'is-preview');
        clone.querySelectorAll('[id]').forEach((element) => {
          element.id = `${element.id}-clone-${cloneIndex}`;
        });
        clone.querySelectorAll('button').forEach((button) => button.removeAttribute('aria-current'));
        this.slider.appendChild(clone);
      });
    }

    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  getRealSlides() {
    return Array.from(this.slider.querySelectorAll('[id^="Slide-"]:not([data-clone])')).filter(
      (element) => element.clientWidth > 0
    );
  }

  getSlideKey(slide) {
    return slide.dataset.mediaId || slide.dataset.target;
  }

  getCurrentRealSlide(realSlides) {
    if (!realSlides.length) return null;

    const viewportCenter = this.slider.scrollLeft + this.slider.clientWidth / 2;
    const visibleSlides = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);

    let closestSlide = visibleSlides[0];
    let closestDistance = Infinity;

    visibleSlides.forEach((slide) => {
      const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
      const distance = Math.abs(slideCenter - viewportCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestSlide = slide;
      }
    });

    if (!closestSlide || closestSlide.hasAttribute('data-clone')) {
      const slideKey = this.getSlideKey(closestSlide);
      return realSlides.find((slide) => this.getSlideKey(slide) === slideKey) || realSlides[0];
    }

    return closestSlide;
  }

  setScrollInstant(position) {
    this.isAdjustingScroll = true;
    this.slider.style.setProperty('scroll-behavior', 'auto');
    this.slider.scrollLeft = position;
    this.slider.style.removeProperty('scroll-behavior');
    this.isAdjustingScroll = false;
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
      behavior: 'smooth',
    });
  }

  scrollToRealSlide(element) {
    if (!element || !this.loopSetWidth) {
      if (element) this.slider.scrollTo({ left: element.offsetLeft });
      return;
    }

    const realSlides = this.getRealSlides();
    const firstRealSlide = realSlides[0];
    if (!firstRealSlide) return;

    const targetLeft = firstRealSlide.offsetLeft + (element.offsetLeft - firstRealSlide.offsetLeft) + this.loopSetWidth;
    this.setScrollInstant(targetLeft);
  }

  measureLoopSet() {
    const slides = this.slider.querySelectorAll('[id^="Slide-"]:not([data-clone])');
    if (slides.length < 1) {
      this.loopSetWidth = 0;
      return;
    }

    const first = slides[0];
    const last = slides[slides.length - 1];
    const nextWidth = last.offsetLeft + last.offsetWidth - first.offsetLeft;

    if (nextWidth <= 0) return;

    const previousWidth = this.loopSetWidth;
    this.loopSetWidth = nextWidth;

    if (!this.scrollPositionInitialized) {
      this.initializeScrollPosition();
      return;
    }

    if (previousWidth > 0 && previousWidth !== nextWidth) {
      const ratio = this.slider.scrollLeft / previousWidth;
      this.setScrollInstant(ratio * nextWidth);
    }
  }

  initializeScrollPosition() {
    if (!this.loopSetWidth || this.scrollPositionInitialized) return;

    this.setScrollInstant(this.loopSetWidth);
    this.scrollPositionInitialized = true;
  }

  onInfiniteScroll() {
    if (this.isAdjustingScroll || !this.loopSetWidth) return;

    const { scrollLeft } = this.slider;
    const setWidth = this.loopSetWidth;
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;

    if (scrollLeft >= setWidth * 2 - 2 || scrollLeft >= maxScroll - 2) {
      this.setScrollInstant(scrollLeft - setWidth);
      return;
    }

    if (scrollLeft <= 2) {
      this.setScrollInstant(scrollLeft + setWidth);
    }
  }

  resetPages() {
    this.slider.querySelectorAll('[data-clone]').forEach((element) => element.remove());
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.scrollPositionInitialized = false;
    this.loopSetWidth = 0;
    this.cloneSlidesForLoop();
    this.initPages();
    this.measureLoopSet();
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    const realSlideCount = this.getRealSlides().length;
    this.totalPages = Math.max(realSlideCount - this.slidesPerPage + 1, 1);
    this.update();
  }

  update() {
    if (!this.slider || !this.nextButton) return;

    const realSlides = this.getRealSlides();
    if (realSlides.length < 1) return;

    const previousPage = this.currentPage;
    const currentRealElement = this.getCurrentRealSlide(realSlides);
    this.currentPage = realSlides.indexOf(currentRealElement) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = realSlides.length;
    }

    if (this.currentPage !== previousPage && currentRealElement) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: currentRealElement,
          },
        })
      );
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!this.sliderItemOffset) this.initPages();

    const step = Number(event.currentTarget.dataset.step || 1);
    const delta = step * this.sliderItemOffset;
    const target =
      event.currentTarget.name === 'next' ? this.slider.scrollLeft + delta : this.slider.scrollLeft - delta;

    this.setSlidePosition(target);
  }
}

customElements.define('product-gallery-infinite-slider', ProductGalleryInfiniteSlider);
